import { AfterViewInit, ApplicationRef, ChangeDetectorRef, Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MessageService } from 'primeng/api';
import { from, Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { Chart, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import * as moment from 'moment';
import { ReportService } from '../..';
import { ConstantService } from '@app/_services/constant.service';
import { OperationsDrilldownDialogComponent } from './operations-drilldown-dialog.component';

Chart.register(...registerables, ChartDataLabels);

// Disable Chart.js animations globally for this dashboard. Each chart
// otherwise spends ~500–1000 ms on its initial entrance animation, and we
// render ~11 charts back-to-back as section responses arrive. Animations on
// that scale block the JS main thread long enough to delay other UI work
// (drilldown response rendering, button clicks). The chart still updates on
// data change — it just doesn't tween in.
(Chart as any).defaults.animation         = false;
(Chart as any).defaults.animations        = {};
(Chart as any).defaults.transitions       = { active: { animation: { duration: 0 } } };
(Chart as any).defaults.responsiveAnimationDuration = 0;

export type DashboardPreset = 'today' | 'yesterday' | 'mtd' | 'next-day' | 'next-week' | 'custom';
export interface LocationOption { id: number | null; name: string; }

/**
 * Operations Dashboard — manager view at /operations-dashboard.
 *
 * Single GET to /api/2.6/operations-dashboard?date=YYYY-MM-DD populates every
 * section. Strict adherence to kbmadmin-style-guide: Inter UI font, JetBrains
 * Mono for every numeric value (via .numeric class on <span>), semantic
 * palette (#10B981 / #F59E0B / #EF4444 / #3B82F6).
 *
 * Date safety: NEVER use bare new Date(); we always go through
 * moment().startOf('day').toDate() per CLAUDE.md guardrail.
 */
@Component({
    selector: 'app-operations-dashboard',
    templateUrl: './operations-dashboard.component.html',
    styleUrls: ['./operations-dashboard.component.scss'],
    providers: [MessageService],
    standalone: false
})
export class OperationsDashboardComponent implements OnInit, AfterViewInit, OnDestroy {

    // Color palette — semantic, NOT decorative. Mirrors kbmadmin-style-guide.
    static readonly COLOR = {
        win:      '#10B981',
        warning:  '#F59E0B',
        danger:   '#EF4444',
        info:     '#3B82F6',
        neutral:  '#6B7280',
        // Housekeeping mix bar — mirrors RoomStatusEnum.color values
        dirty:        '#8B0000',
        housekeeping: '#FF69B4',
        inspection:   '#9333EA',
        ready:        '#10B981',
    };

    @ViewChild('futonDonut',    { static: false }) futonDonutEl!: ElementRef<HTMLCanvasElement>;
    @ViewChild('rollawayDonut', { static: false }) rollawayDonutEl!: ElementRef<HTMLCanvasElement>;
    @ViewChild('futonSpark',    { static: false }) futonSparkEl!: ElementRef<HTMLCanvasElement>;
    @ViewChild('rollawaySpark', { static: false }) rollawaySparkEl!: ElementRef<HTMLCanvasElement>;
    @ViewChild('occLine',       { static: false }) occLineEl!: ElementRef<HTMLCanvasElement>;
    @ViewChild('beddingLine',   { static: false }) beddingLineEl!: ElementRef<HTMLCanvasElement>;
    @ViewChild('cancelBar',     { static: false }) cancelBarEl!: ElementRef<HTMLCanvasElement>;
    @ViewChild('channelDonut',  { static: false }) channelDonutEl!: ElementRef<HTMLCanvasElement>;
    // Future Occupancy Report charts (mirror /future-occupancy-report)
    @ViewChild('foMonthlyBar',  { static: false }) foMonthlyBarEl!: ElementRef<HTMLCanvasElement>;
    @ViewChild('foTypePie',     { static: false }) foTypePieEl!: ElementRef<HTMLCanvasElement>;
    @ViewChild('foCheckLine',   { static: false }) foCheckLineEl!: ElementRef<HTMLCanvasElement>;
    @ViewChild('foCheckInByBed',{ static: false }) foCheckInByBedEl!: ElementRef<HTMLCanvasElement>;
    @ViewChild('foCheckOutByBed',{ static: false }) foCheckOutByBedEl!: ElementRef<HTMLCanvasElement>;

    private charts: Chart[] = [];
    /** In-flight section requests — cancelled on next load() so refresh clicks
     *  don't pile up while slow requests are still running. One subscription
     *  per parallel section fetch. */
    private loadSubs: Subscription[] = [];

    /**
     * Section keys, ordered for two competing goals:
     *   1. Start the heaviest sections FIRST so they don't queue at the tail.
     *      futureOccupancyReport / housekeepingLinen / staffing are the
     *      slowest — fire them while we still have the full concurrency window.
     *   2. Within each tier, prefer the visually-most-important sections so
     *      the user sees movement immediately.
     */
    readonly sectionNames: string[] = [
        // Heavy — fire first so they finish before the user clicks anything
        'futureOccupancyReport', 'housekeepingLinen', 'staffing',
        // Visually important / medium cost — second wave
        'kpis', 'equipment', 'locations',
        // Light — last
        // dataHealth fetched until the section was removed per manager
        // request; drop from the loader to skip the round-trip. Backend
        // still produces it for the legacy all-in-one endpoint and any
        // external consumer that asks for it explicitly.
        'housekeeping', 'alerts', 'revenueSignals',
        'trends', 'guestExperience', 'futureOccupancy',
        'maintenance', 'amenities',
    ];

    /** Subset of sections whose charts should render as soon as their data arrives. */
    private readonly chartRenderers: { [name: string]: () => void } = {};

    loading = false;
    /** Per-section loading flag — drives optional skeletons in the template. */
    sectionLoading: { [name: string]: boolean } = {};
    /** Per-section error message — shown inline beside the section title. */
    sectionError: { [name: string]: string | null } = {};
    /**
     * Number of sections still in flight — kept as a field instead of a
     * template function so Angular change detection doesn't recompute it on
     * every cycle. Refreshed inside recomputeOverallLoading whenever a
     * section's loading flag flips.
     */
    inFlightCount = 0;
    error: string | null = null;
    data: any = null;

    // Future Occupancy Report — tab state: 'overview' (charts) or 'reservations' (table)
    foTab: 'overview' | 'reservations' = 'overview';

    /** Linen sizes in display order (template iterates this). */
    readonly linenSizeOrder: string[] = ['King', 'Queen', 'Full', 'Twin'];

    /**
     * Linen detail filter state. When a size is selected the per-unit table
     * shows only units that contribute to that size. 'Total' means all units.
     * null = same as 'Total' but no tile is highlighted yet.
     */
    selectedLinenSize: string | null = null;

    // Filter state
    selectedPreset: DashboardPreset = 'today';
    startDate: Date = moment().startOf('day').toDate();
    endDate:   Date = moment().startOf('day').toDate();
    selectedLocationId: number | null = null;
    compareWow = false;

    /**
     * Canonical locationId → name mapping. Mirrors:
     *   • ConstantService.locations (the app-wide source of truth)
     *   • LOCATION_NAMES in OperationsDashboardBL.java (backend mirror)
     *   • The "Location" column rendered in the per-location table
     *
     * The previous mapping invented "West / South / Upcountry Maui" splits
     * which don't exist in the DB — locationId=1 is the whole Maui island,
     * locationId=3 is Big Island, etc. The old labels caused Big Island's
     * drilldown title to read "Upcountry Maui" because the locationName()
     * helper resolved id=3 to the old (wrong) name. Keep this list in sync
     * with the backend's LOCATION_NAMES whenever a new location is added.
     */
    locationOptions: LocationOption[] = [
        { id: null, name: 'All Locations' },
        { id: 1,    name: 'Maui' },
        { id: 2,    name: 'Park City' },
        { id: 3,    name: 'Big Island' },
        { id: 4,    name: 'Oahu' },
        { id: 5,    name: 'San Diego' },
        { id: 6,    name: 'Aspen' },
        { id: 9,    name: 'Bass Lake' },
        { id: 10,   name: 'Yosemite' },
    ];

    presets: { key: DashboardPreset; label: string }[] = [
        { key: 'today',     label: 'Today' },
        { key: 'yesterday', label: 'Yesterday' },
        { key: 'mtd',       label: 'MTD' },
        { key: 'next-day',  label: 'Next Day' },
        { key: 'next-week', label: 'Next Week' },
        { key: 'custom',    label: 'Custom' },
    ];

    constructor(
        private reportService: ReportService,
        private messageService: MessageService,
        private router: Router,
        private dialog: MatDialog,
        private cdr: ChangeDetectorRef,
        private zone: NgZone,
        private appRef: ApplicationRef,
        // Public so the template can call constantService.refSrcImages directly if
        // we ever inline a lookup; today the channelIcon / channelLabel helpers
        // below wrap the find() so the template stays simple.
        public constantService: ConstantService) {
        // Map section name -> chart render method, used by load() to paint
        // each section's charts as soon as its payload arrives.
        this.chartRenderers['equipment']             = () => this.renderEquipmentCharts();
        this.chartRenderers['trends']                = () => this.renderTrendCharts();
        this.chartRenderers['revenueSignals']        = () => this.renderRevenueCharts();
        this.chartRenderers['futureOccupancyReport'] = () => this.renderFoCharts();
    }

    ngOnInit(): void {
        this.applyPreset('today');
    }

    ngAfterViewInit(): void {
        // Charts render after data lands; no-op here.
    }

    ngOnDestroy(): void {
        this.destroyCharts();
        this.cancelInflight();
    }

    /** Tear down every in-flight section subscription. Idempotent. */
    private cancelInflight(): void {
        for (const s of this.loadSubs) { try { s.unsubscribe(); } catch (e) { /* swallow */ } }
        this.loadSubs = [];
    }

    applyPreset(preset: DashboardPreset): void {
        this.selectedPreset = preset;
        const today = moment().startOf('day');
        switch (preset) {
            case 'today':     this.startDate = today.toDate();                 this.endDate = today.toDate(); break;
            case 'yesterday': this.startDate = today.clone().subtract(1, 'day').toDate(); this.endDate = today.clone().subtract(1, 'day').toDate(); break;
            case 'mtd':       this.startDate = today.clone().startOf('month').toDate();  this.endDate = today.toDate(); break;
            case 'next-day':  this.startDate = today.clone().add(1, 'day').toDate();     this.endDate = today.clone().add(1, 'day').toDate(); break;
            case 'next-week': this.startDate = today.clone().add(1, 'day').toDate();     this.endDate = today.clone().add(7, 'days').toDate(); break;
            case 'custom':    /* keep current dates */ break;
        }
        this.load();
    }

    onCustomRangeChange(): void {
        if (this.startDate && this.endDate) {
            this.selectedPreset = 'custom';
            this.load();
        }
    }

    onLocationChange(): void {
        this.load();
    }

    onCompareWowChange(): void {
        this.load();
    }

    load(): void {
        // Cancel any in-flight section requests before starting a new batch —
        // rapid Refresh clicks would otherwise pile up dozens of subscriptions.
        this.cancelInflight();
        this.destroyCharts();
        this.error = null;
        this.loading = true;

        // Reset progressive state — sections will paint as their responses
        // arrive, in roughly the order the server processes them.
        const opts = {
            startDate:  moment(this.startDate).startOf('day').toDate(),
            endDate:    moment(this.endDate).startOf('day').toDate(),
            locationId: this.selectedLocationId,
            compareWow: this.compareWow,
        };
        // Preserve the top-level meta fields (startDate, endDate, locationId,
        // generatedAt) once we get them from the first response. Until then,
        // populate from the request itself so any template guards work.
        this.data = {
            startDate:  moment(opts.startDate).format('YYYY-MM-DD'),
            endDate:    moment(opts.endDate).format('YYYY-MM-DD'),
            locationId: opts.locationId,
        };
        this.sectionLoading = {};
        this.sectionError = {};
        for (const name of this.sectionNames) { this.sectionLoading[name] = true; this.sectionError[name] = null; }

        // Stream the section requests through a small concurrency window
        // (CONCURRENCY) instead of firing all 14 at once. Browser HTTP/1.1
        // limits to 6 concurrent connections per origin; we cap at 4 so
        // there are always at least 2 free slots for user-triggered actions
        // (drill-downs, other tabs in the app, refresh while loading). We
        // process the heaviest sections first so the slow ones don't starve
        // the tail. Together with no-retry on the section endpoint (see
        // ReportService), this keeps the browser queue from saturating.
        const CONCURRENCY = 4;
        const handleSection = (name: string, res: any, err: any): void => {
            this.zone.run(() => {
                if (err) {
                    this.sectionError[name] = err?.message || 'Network error loading section';
                } else if (res && res.success && res.data) {
                    if (res.data.startDate)   this.data.startDate   = res.data.startDate;
                    if (res.data.endDate)     this.data.endDate     = res.data.endDate;
                    if (res.data.generatedAt) this.data.generatedAt = res.data.generatedAt;
                    if (Object.prototype.hasOwnProperty.call(res.data, name)) {
                        this.data[name] = res.data[name];
                    }
                    // WoW path piggy-backs on the kpis request.
                    if (name === 'kpis' && res.data.kpisLastWeek) {
                        this.data.kpisLastWeek = res.data.kpisLastWeek;
                    }
                    // If this section drives any charts, render them on the
                    // next change-detection tick so *ngIf-gated canvas
                    // elements have a chance to mount.
                    const r = this.chartRenderers[name];
                    if (r) setTimeout(() => r(), 0);
                } else {
                    this.sectionError[name] = (res && res.error) || 'Failed to load section';
                }
                this.sectionLoading[name] = false;
                this.recomputeOverallLoading();
                this.cdr.detectChanges();
            });
        };
        const streamSub = from(this.sectionNames).pipe(
            mergeMap(
                (name) => new Promise<void>((resolve) => {
                    const sectionSub = this.reportService.operationsDashboardSection(name, opts).subscribe({
                        next: (res) => { handleSection(name, res, null); resolve(); },
                        error: (err) => { handleSection(name, null, err); resolve(); },
                    });
                    this.loadSubs.push(sectionSub);
                }),
                CONCURRENCY
            )
        ).subscribe();
        this.loadSubs.push(streamSub);
    }

    /**
     * Overall loading flag + in-flight count. Single pass over sectionNames so
     * we don't iterate twice when both values are needed (was previously two
     * separate iterations called from the template on every CD cycle, which
     * compounded into noticeable jank when 14 sections completed back-to-back).
     */
    private recomputeOverallLoading(): void {
        let n = 0;
        for (const name of this.sectionNames) { if (this.sectionLoading[name]) n++; }
        this.inFlightCount = n;
        this.loading = n > 0;
    }

    /** Coalesces rapid double-clicks within this window into one open. */
    private static readonly DRILLDOWN_DEBOUNCE_MS = 250;
    private lastDrilldownOpenAt = 0;

    /**
     * Open the drill-down dialog for a clicked KPI. Pass an explicit locationId
     * (single-location filter) to drill into a row of one of the per-location
     * tables; pass null/undefined to use the dashboard's current location
     * filter (which may itself be null = portfolio-wide).
     */
    /** Tracks a parent-side drilldown fetch so we can show a spinner / cancel. */
    private drilldownFetchSub: Subscription | null = null;
    drilldownLoading = false;

    /**
     * Inline drill-down panel state — flattened to top-level fields because
     * binding to NESTED object properties (drilldownPanel.loading) failed to
     * propagate to the DOM in this Angular build even after detectChanges()
     * + appRef.tick() + deferred passes. Top-level booleans are evaluated
     * differently by the CD machinery and are guaranteed to update.
     */
    drilldownPanelOpen = false;
    drilldownPanelLoading = false;
    drilldownPanelMetric: string | null = null;
    drilldownPanelTitle = '';
    drilldownPanelError: string | null = null;
    drilldownPanelRows: any[] = [];
    drilldownPanelLocationId: number | null = null;
    drilldownPanelStartDate: Date | null = null;
    drilldownPanelEndDate: Date | null = null;

    /** Reservation-status code → readable name (mirrors ShortTermRentalVO). */
    private readonly DRILLDOWN_RES_STATUS_NAMES: { [code: string]: string } = {
        'QO': 'Quote Open', 'QH': 'Quote Hold', 'QE': 'Quote Expired',
        'OP': 'Reserved', 'OC': 'Sale Closed', 'PP': 'Sale Postponed',
        'OX': 'Cancelled', 'NA': 'Maintenance',
        'OO': 'Owner Occupied', 'OG': 'Owner Guest Occupied',
    };
    /** RoomStatusEnum code → readable name. */
    private readonly DRILLDOWN_ROOM_STATUS_NAMES: { [code: number]: string } = {
        1: 'Occupied', 2: 'Dirty', 3: 'Housekeeping',
        4: 'Ready for Inspection', 5: 'Inspection', 6: 'Clean / Ready',
        7: 'Guest Checked-In', 8: 'In Progress', 9: 'Not Available',
    };

    /** RoomStatusEnum code → background colour. Mirrors / extends
     *  OperationsDashboardComponent.COLOR so a status pill in any table
     *  matches the housekeeping mix-bar visually. Defaults to '' (no inline
     *  style) for unknown codes so a future status doesn't crash the cell. */
    private readonly ROOM_STATUS_COLORS: { [code: number]: string } = {
        1: '#1E40AF',   // Occupied              — info blue (someone is in the unit)
        2: '#8B0000',   // Dirty                 — dark red    (matches mix bar)
        3: '#FF69B4',   // Housekeeping          — hot pink    (matches mix bar)
        4: '#9333EA',   // Ready for Inspection  — purple
        5: '#9333EA',   // Inspection            — purple      (matches mix bar)
        6: '#10B981',   // Clean / Ready         — green       (matches mix bar)
        7: '#1E40AF',   // Guest Checked-In      — info blue
        8: '#F59E0B',   // In Progress           — amber
        9: '#6B7280',   // Not Available         — neutral grey
    };

    /** Readable name for a RoomStatusEnum code (e.g. 2 → "Dirty"). Returns
     *  the raw code as a string when unknown so nothing is hidden silently. */
    roomStatusName(code: number | string | null | undefined): string {
        if (code == null) return '—';
        const n = Number(code);
        return this.DRILLDOWN_ROOM_STATUS_NAMES[n] || String(code);
    }

    /** Background colour for a RoomStatusEnum cell. '' = no inline style. */
    roomStatusColor(code: number | string | null | undefined): string {
        if (code == null) return '';
        return this.ROOM_STATUS_COLORS[Number(code)] || '';
    }

    drilldown(metric: string, titleOverride?: string, locationIdOverride?: number | null,
              startDateOverride?: Date, endDateOverride?: Date): void {
        // Debounce rapid duplicate clicks.
        const now = performance.now();
        if (now - this.lastDrilldownOpenAt < OperationsDashboardComponent.DRILLDOWN_DEBOUNCE_MS) return;
        this.lastDrilldownOpenAt = now;

        // Cancel any prior in-flight drill-down fetch.
        if (this.drilldownFetchSub) { try { this.drilldownFetchSub.unsubscribe(); } catch (e) {} this.drilldownFetchSub = null; }

        const effectiveLocId = (locationIdOverride !== undefined) ? locationIdOverride : this.selectedLocationId;
        const baseTitle = titleOverride || this.metricTitle(metric);
        const locName = this.locationName(effectiveLocId);
        const title = locName ? `${baseTitle} — ${locName}` : baseTitle;
        // Date overrides let stat cards (e.g. pickup "Today" / "Yesterday" /
        // "Last 7d") drill into a window narrower than the dashboard's
        // selected range without the caller having to mutate component state.
        const startDate = startDateOverride
            ? moment(startDateOverride).startOf('day').toDate()
            : moment(this.startDate).startOf('day').toDate();
        const endDate   = endDateOverride
            ? moment(endDateOverride).startOf('day').toDate()
            : moment(this.endDate).startOf('day').toDate();

        // Fetch FIRST, then navigate to the drill-down page with all data in
        // router state. The drill-down page mounts fresh with everything
        // pre-loaded so it never has to do a post-mount state transition
        // (which this codebase's change detection mysteriously can't handle).
        this.drilldownLoading = true;
        try { this.cdr.detectChanges(); } catch (e) {}

        this.drilldownFetchSub = this.reportService.operationsDashboardDrilldown(metric, {
            startDate, endDate, locationId: effectiveLocId,
        }).subscribe({
            next: (res: any) => this.zone.run(() => {
                const rows = (res && res.success && res.data && res.data.rows) || [];
                const error = (res && !res.success) ? (res.error || 'Unknown error') : null;
                this.drilldownLoading = false;
                this.drilldownFetchSub = null;
                this.navigateToDrilldownPage(metric, title, startDate, endDate, effectiveLocId, rows, error);
            }),
            error: (e) => this.zone.run(() => {
                this.drilldownLoading = false;
                this.drilldownFetchSub = null;
                this.navigateToDrilldownPage(metric, title, startDate, endDate, effectiveLocId, [], e?.message || 'Network error');
            }),
        });
    }

    /**
     * Open the full-page drill-down in a new browser tab.
     *
     * history.state doesn't cross tab boundaries (each new tab gets a fresh
     * History stack), so we use localStorage with a one-shot unique key to
     * hand the payload to the new tab. The drill-down page reads + deletes
     * the entry on mount, leaving no residue.
     */
    private navigateToDrilldownPage(metric: string, title: string, startDate: Date, endDate: Date,
                                     locationId: number | null, rows: any[], error: string | null): void {
        const key = 'kbma-drilldown-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
        const payload = {
            metric, title, rows, error, locationId,
            startDate: startDate?.toISOString(),
            endDate: endDate?.toISOString(),
        };
        try {
            localStorage.setItem(key, JSON.stringify(payload));
        } catch (e) {
            console.error('[drilldown] localStorage write failed', e);
        }
        const tree = this.router.createUrlTree(['/operations-dashboard/drilldown'], { queryParams: { key } });
        const url = this.router.serializeUrl(tree);
        window.open(url, '_blank', 'noopener,noreferrer');
    }

    /** Close the inline drill-down panel and cancel any in-flight fetch. */
    closeDrilldownPanel(): void {
        if (this.drilldownFetchSub) { try { this.drilldownFetchSub.unsubscribe(); } catch (e) {} this.drilldownFetchSub = null; }
        this.drilldownPanelOpen = false;
        this.drilldownPanelLoading = false;
        this.drilldownPanelMetric = null;
        this.drilldownPanelRows = [];
        this.drilldownPanelError = null;
        this.drilldownLoading = false;
        this.cdr.detectChanges();
    }

    /** ESC closes the inline drill-down panel. */
    @HostListener('document:keydown.escape')
    onEscape(): void {
        if (this.drilldownPanelOpen) this.closeDrilldownPanel();
    }

    /** Columns to render for the inline drill-down panel based on metric. */
    drilldownColumns(metric: string): { field: string; header: string; type?: string }[] {
        switch (metric) {
            case 'arrivals':
            case 'departures':
                return [
                    { field: 'reservationId',      header: 'Res ID' },
                    { field: 'propertyCode',       header: 'Property' },
                    { field: 'guestFirstName',     header: 'First' },
                    { field: 'guestLastName',      header: 'Last' },
                    { field: 'checkInDateTime',    header: 'Check-in',  type: 'checkInPolicy' },
                    { field: 'checkOutDateTime',   header: 'Check-out', type: 'checkOutPolicy' },
                    { field: 'earlyCheckInTag',    header: 'Early Check-in' },
                    { field: 'lateCheckoutTag',    header: 'Late Checkout' },
                    { field: 'numberOfNightsStay', header: 'Nights',    type: 'num'  },
                    { field: 'reservationStatus',  header: 'Status',    type: 'resStatus' },
                ];
            case 'occupied':
            case 'vacantClean':
            case 'outOfOrder':
            case 'cleaningBacklog':
            case 'allUnits':
            case 'unitsDirty':
            case 'unitsHousekeeping':
            case 'unitsInspection':
                return [
                    { field: 'shortTermRentalId', header: 'STR ID',   type: 'propertyLink' },
                    { field: 'propertyCode',      header: 'Property' },
                    { field: 'propertyName',      header: 'Name' },
                    { field: 'bedrooms',          header: 'Bedrooms', type: 'num' },
                    { field: 'occupancyStatus',   header: 'Status',   type: 'roomStatus' },
                ];
            case 'unitsUnsetStatus':
                return [
                    { field: 'shortTermRentalId', header: 'STR ID',     type: 'propertyLink' },
                    { field: 'propertyCode',      header: 'Property' },
                    { field: 'propertyName',      header: 'Name' },
                    { field: 'bedrooms',          header: 'Bedrooms',   type: 'num' },
                    // Show the raw status code so a non-NULL but unknown value is
                    // visible (e.g. a future enum addition not yet in Java code).
                    { field: 'occupancyStatus',   header: 'Raw Status' },
                    { field: 'lastUpdate',        header: 'Last Update', type: 'date' },
                ];
            case 'futonsEligible':
            case 'rollawaysEligible':
                return [
                    { field: 'shortTermRentalId', header: 'STR ID',   type: 'propertyLink' },
                    { field: 'propertyCode',      header: 'Property' },
                    { field: 'propertyName',      header: 'Name' },
                    { field: 'bedrooms',          header: 'Bedrooms', type: 'num' },
                    { field: 'capacity',          header: 'Capacity', type: 'num' },
                ];
            case 'futonsRevenue':
            case 'rollawaysRevenue':
                return [
                    { field: 'orderId',          header: 'Order' },
                    { field: 'propertyCode',     header: 'Property' },
                    { field: 'guestFirstName',   header: 'First' },
                    { field: 'guestLastName',    header: 'Last' },
                    { field: 'checkInDateTime',  header: 'Check-in',   type: 'date' },
                    { field: 'checkOutDateTime', header: 'Check-out',  type: 'date' },
                    { field: 'productName',      header: 'Product' },
                    { field: 'quantity',         header: 'Qty',        type: 'num' },
                    { field: 'listPrice',        header: 'List Price', type: 'money' },
                    { field: 'lineTotal',        header: 'Line Total', type: 'money' },
                    { field: 'saleDate',         header: 'Booked',     type: 'date' },
                ];
            case 'guestsOnProperty':
                return [
                    { field: 'reservationId',    header: 'Res ID' },
                    { field: 'propertyCode',     header: 'Property' },
                    { field: 'guestFirstName',   header: 'First' },
                    { field: 'guestLastName',    header: 'Last' },
                    { field: 'bedrooms',         header: 'Bedrooms',   type: 'num' },
                    { field: 'checkInDateTime',  header: 'Check-in',   type: 'checkInPolicy' },
                    { field: 'checkOutDateTime', header: 'Check-out',  type: 'checkOutPolicy' },
                    { field: 'earlyCheckInTag',  header: 'Early Check-in' },
                    { field: 'lateCheckoutTag',  header: 'Late Checkout' },
                ];
            case 'futonsOut':
            case 'rollawaysOut':
                return [
                    { field: 'reservationId',    header: 'Res ID' },
                    { field: 'propertyCode',     header: 'Property' },
                    { field: 'productName',      header: 'Product' },
                    { field: 'quantity',         header: 'Qty', type: 'num' },
                    { field: 'guestFirstName',   header: 'First' },
                    { field: 'guestLastName',    header: 'Last' },
                    { field: 'checkInDateTime',  header: 'Check-in',  type: 'checkInPolicy' },
                    { field: 'checkOutDateTime', header: 'Check-out', type: 'checkOutPolicy' },
                ];
            case 'cancellations':
                return [
                    { field: 'reservationId',    header: 'Res ID' },
                    { field: 'propertyCode',     header: 'Property' },
                    { field: 'guestFirstName',   header: 'First' },
                    { field: 'guestLastName',    header: 'Last' },
                    { field: 'cancelDate',       header: 'Cancelled',     type: 'date' },
                    { field: 'checkInDateTime',  header: 'Was Check-in',  type: 'checkInPolicy' },
                    { field: 'revenueLost',      header: 'Revenue Lost',  type: 'money' },
                ];
            case 'bookingsCreated':
                return [
                    { field: 'orderId',          header: 'Order' },
                    { field: 'saleDate',         header: 'Booked',   type: 'date' },
                    // refSrcId rendered via 'channel' formatter → "VRBO" /
                    // "Airbnb" / "Website" / "Direct" etc. Previously the
                    // column showed o.type (OrderType code, e.g. "ST" = Short
                    // Term listing) which is unrelated to channel.
                    { field: 'refSrcId',         header: 'Channel',  type: 'channel' },
                    { field: 'propertyCode',     header: 'Property' },
                    { field: 'guestFirstName',   header: 'First' },
                    { field: 'guestLastName',    header: 'Last' },
                    { field: 'checkInDateTime',  header: 'Check-in', type: 'checkInPolicy' },
                    { field: 'status',           header: 'Status' },
                ];
            case 'sameDayTurnovers':
                return [
                    { field: 'propertyCode',       header: 'Property' },
                    { field: 'departingResId',     header: 'Departing Res' },
                    { field: 'departingLastName',  header: 'Departing Guest' },
                    { field: 'arrivingResId',      header: 'Arriving Res' },
                    { field: 'arrivingLastName',   header: 'Arriving Guest' },
                ];
            case 'priorityRooms':
                return [
                    { field: 'propertyCode',     header: 'Property' },
                    { field: 'reservationId',    header: 'Res ID' },
                    { field: 'occupancyStatus',  header: 'Status',   type: 'roomStatus' },
                    { field: 'checkInDateTime',  header: 'Check-in', type: 'checkInPolicy' },
                ];
            case 'reviews':
                return [
                    { field: 'reviewDate',       header: 'Date',     type: 'date' },
                    { field: 'propertyCode',     header: 'Property' },
                    { field: 'guestFirstName',   header: 'First' },
                    { field: 'guestLastName',    header: 'Last' },
                    { field: 'rating',           header: 'Rating',   type: 'rating' },
                    { field: 'reviewSource',     header: 'Source' },
                    { field: 'title',            header: 'Title' },
                    { field: 'reviewDesc',       header: 'Review' },
                ];
            default:
                return Object.keys((this.drilldownPanelRows[0]) || {}).map(k => ({ field: k, header: k }));
        }
    }

    /** Format a single cell value for the drill-down panel. */
    drilldownFormat(row: any, col: { field: string; type?: string }): string {
        const v = row[col.field];
        if (v == null) return '—';
        switch (col.type) {
            case 'date':       return new Date(v).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' });
            case 'num':        return Number(v).toLocaleString('en-US');
            case 'money':      return Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
            case 'resStatus':  return this.DRILLDOWN_RES_STATUS_NAMES[String(v)] || String(v);
            case 'roomStatus': return this.DRILLDOWN_ROOM_STATUS_NAMES[Number(v)] || String(v);
            case 'propertyLink': return String(v);
            case 'rating': {
                const scale = row['ratingScale'] ?? 5;
                return `${Number(v).toFixed(2)} / ${scale}`;
            }
            // KBM policy times — see operations-drilldown-page format() for
            // the rationale. Date-only midnight in DB → UTC-anchored M/D/YY
            // plus fixed 4 PM / 11 AM. Without this the panel shows the
            // browser's tz-shifted midnight, which is misleading.
            case 'checkInPolicy':  return this.formatDateUTC(v) + ', 4:00 PM';
            case 'checkOutPolicy': return this.formatDateUTC(v) + ', 11:00 AM';
            case 'channel': {
                if (v == null) return 'Direct';
                const num = Number(v);
                const match = this.constantService.refSrcImages.find(
                    (x: any) => x.value === num);
                return match ? match.label : `Source ${num}`;
            }
            default:           return String(v);
        }
    }

    /** UTC-anchored M/D/YY formatter — see drilldown-page equivalent. */
    private formatDateUTC(v: any): string {
        const d = new Date(v);
        return d.toLocaleDateString('en-US', {
            month: 'numeric', day: 'numeric', year: '2-digit', timeZone: 'UTC',
        });
    }

    /** Look up a human-friendly location label by id. Returns '' for null/All. */
    locationName(locId: number | null | undefined): string {
        if (locId == null) return '';
        const match = this.locationOptions.find(o => o.id === locId);
        return match ? match.name : '';
    }

    /**
     * Map a column on the per-location staffing/operations table to its
     * drill-down metric key. Cells that aren't meaningfully drillable
     * (e.g. pool size constants) return null so the template skips the click.
     */
    cellMetric(column: string): string | null {
        switch (column) {
            case 'total':        return 'allUnits';
            case 'occupied':     return 'occupied';
            case 'occupancyPct': return 'occupied';
            case 'arrivals':     return 'arrivals';
            case 'departures':   return 'departures';
            case 'guests':       return 'guestsOnProperty';
            case 'futonsOut':    return 'futonsOut';
            case 'rollawaysOut': return 'rollawaysOut';
            case 'sameDayTo':    return 'sameDayTurnovers';
            case 'cleansToday':  return 'cleaningBacklog';
            case 'housekeeper':  return 'cleaningBacklog';
            case 'inspector':    return 'cleaningBacklog';
            case 'guestSvc':     return 'arrivals';
            case 'maint':        return 'occupied';
            case 'linens':       return 'arrivals';
            case 'towels':       return 'arrivals';
            case 'amenities':    return 'arrivals';
            default:             return null;
        }
    }

    /** Friendly column-label lookup for the drill-down dialog title. */
    cellLabel(column: string): string {
        const map: { [k: string]: string } = {
            total:        'All Active Units',
            occupied:     'Occupied Rooms',
            occupancyPct: 'Occupied Rooms',
            arrivals:     'Arrivals',
            departures:   'Departures',
            guests:       'Guests on Property',
            futonsOut:    'Futons Out',
            rollawaysOut: 'Rollaways Out',
            sameDayTo:    'Same-Day Turnovers',
            cleansToday:  'Cleaning Queue',
            housekeeper:  'Housekeeping Workload',
            inspector:    'Inspection Workload',
            guestSvc:     'Arrivals (Guest Services)',
            maint:        'Occupied Units (Maintenance)',
            linens:       'Arrivals (Linens)',
            towels:       'Arrivals (Towels)',
            amenities:    'Arrivals (Amenities)',
        };
        return map[column] || column;
    }

    /**
     * Cell click handler — bound to every numeric cell in the per-location
     * tables (Operations-by-Location and Staffing).
     *
     * Reservation-shaped columns (arrivals / departures / occupied / guests)
     * route to /reservations with the location override applied so the
     * manager works the canonical reservation list, not a proprietary
     * drilldown. Unit-shaped and order-shaped columns (futons / rollaways /
     * housekeeping queues / sameDayTo) still open the dashboard's drilldown
     * page because /reservations has no equivalent filter for them.
     */
    cellDrill(column: string, locId: number | null): void {
        // Route reservation-shaped cells to /reservations with the row's
        // location override. Property-shaped cells route to /property-list.
        switch (column) {
            case 'total':
                // "All Active Units" → /property-list filtered to this
                // location. Manager works the canonical property table.
                this.openPropertyListForLocation(locId);
                return;
            case 'arrivals':
            // Staffing-table columns whose underlying workload is "today's
            // arrivals" — Guest Services FTE, Linens, Towels recommendations.
            // Manager clicking these wants to see the arrivals that drove
            // the recommended count.
            case 'guestSvc':
            case 'linens':
            case 'towels':
            case 'amenities':
                this.openReservationsForArrivals(undefined, locId);
                return;
            case 'departures':
                this.openReservationsForDepartures(undefined, locId);
                return;
            case 'occupied':
            case 'guests':
                // Both columns count the same canonical reservation set
                // (OP/NA/OO/OG stay-overlap). Same scoped list answers both.
                this.openReservationsList({
                    status: 'OP,NA,OO,OG',
                    stayDateStart: this.startDate,
                    stayDateEnd:   this.endDate,
                    locationId:    locId,
                });
                return;
            case 'maint':
                // Staffing-table maintenance column → off-market units
                // (status=NA spanning today). Same shape as the Maintenance
                // section's per-location chip.
                this.openReservationsForMaintenance(locId);
                return;
        }
        // Non-reservation columns — unit-shaped (sameDayTo, cleansToday,
        // housekeeper, inspector) and order-shaped (futonsOut, rollawaysOut)
        // keep the proprietary drilldown until /reservations grows a
        // matching filter for them.
        const metric = this.cellMetric(column);
        if (!metric) return;
        this.drilldown(metric, this.cellLabel(column), locId);
    }

    /** Compose guest name from first + last with safe fallback. */
    guestName(u: any): string {
        const first = (u && u.guestFirstName) ? u.guestFirstName : '';
        const last  = (u && u.guestLastName)  ? u.guestLastName  : '';
        const combined = `${first} ${last}`.trim();
        return combined.length ? combined : '—';
    }

    /**
     * Map a staffing role label to the drill-down metric that surfaces the
     * underlying workload. Each tile in the staffing strip is clickable; the
     * dialog shows the reservations / units that drove the recommended count.
     */
    staffingDrilldownMetric(role: string): string {
        switch (role) {
            case 'Housekeeper':          return 'cleaningBacklog';   // dirty + housekeeping queue
            case 'Inspector':            return 'cleaningBacklog';   // shares the housekeeping queue
            case 'Guest Services':       return 'arrivals';          // arrivals in range
            case 'Maintenance Houseman': return 'occupied';          // currently occupied units
            default:                      return 'arrivals';
        }
    }

    /**
     * Reservation status code → display name. Mirrors
     * ConstantService.reservationStatuses (the canonical labels used on the
     * /reservations list). Used by the Future Occupancy reservations table so
     * the status column shows "Reserved" instead of the raw "OP" code.
     * Unknown codes fall through to the raw value so a new status still
     * renders, just without a friendly label.
     */
    private readonly RES_STATUS_NAMES: { [code: string]: string } = {
        'QO': 'Quote Open',
        'QH': 'Quote Hold',
        'QE': 'Quote Expired',
        'OP': 'Reserved',
        'OC': 'Sale Closed',
        'PP': 'Sale Postponed',
        'OX': 'Cancelled',
        'NA': 'Maintenance',      // Renamed from "Not Available" — KBM ops use this label
        'OO': 'Owner Occupied',
        'OG': 'Owner Guest Occupied',
    };
    reservationStatusName(code: string | null | undefined): string {
        if (!code) return '—';
        return this.RES_STATUS_NAMES[code] || code;
    }

    /**
     * Background colour for a reservation-status cell. Mirrors
     * ConstantService.reservationStatuses so cells in the Future Occupancy
     * table (and any other dashboard surface that renders reservationStatus)
     * look identical to the canonical /reservations list. Empty string for
     * unknown codes / null so the binding produces no inline style.
     */
    private readonly RES_STATUS_COLORS: { [code: string]: string } = {
        'QO': 'lightgreen', 'QH': 'yellow',    'QE': 'purple',
        'OP': 'lightgrey',  'OC': 'green',     'PP': 'lightblue',
        'OX': 'darkred',    'NA': 'darkgrey',
        'OO': 'hotpink',    'OG': 'lightpink',
    };
    reservationStatusColor(code: string | null | undefined): string {
        if (code == null) return '';
        return this.RES_STATUS_COLORS[String(code)] || '';
    }

    /**
     * Channel-icon URL for a reservation's referralSourceId. Same lookup the
     * Sales / Upsell dashboards use (constantService.refSrcImages) so the icon
     * for, say, Airbnb is identical across surfaces. Returns '' when the source
     * is unknown so the template can hide the <img>.
     */
    channelIcon(refSrcId: number | null | undefined): string {
        if (refSrcId == null) return '';
        const match = this.constantService.refSrcImages.find((x: any) => x.value === refSrcId);
        return match ? match.imgurl : '';
    }
    /** Readable label for the source (Airbnb / VRBO / Booking.com / Website / etc.).
     *  Used as the alt-text / tooltip on the icon. */
    channelLabel(refSrcId: number | null | undefined): string {
        if (refSrcId == null) return '';
        const match = this.constantService.refSrcImages.find((x: any) => x.value === refSrcId);
        return match ? match.label : String(refSrcId);
    }

    /**
     * Build the URL for the order page (used by Res ID links in the Future
     * Occupancy reservations table). Returns 'javascript:void(0)' for null
     * orderIds so the anchor stays safe to render and click without
     * navigating anywhere.
     */
    orderUrl(orderId: any): string {
        if (orderId == null || orderId === '') return 'javascript:void(0)';
        return this.router.serializeUrl(this.router.createUrlTree(['/order', orderId]));
    }

    /**
     * Build the URL for the property-edit page. Used by property-code links
     * in the per-unit linen detail table (and anywhere else we want to jump
     * from a property code to its edit screen). Same fallback semantics as
     * orderUrl — null/empty returns a safe no-op href.
     */
    propertyEditUrl(strId: any): string {
        if (strId == null || strId === '') return 'javascript:void(0)';
        return this.router.serializeUrl(this.router.createUrlTree(['/property-edit', strId]));
    }

    /**
     * Click handler for the linen size tiles. Toggles the per-unit filter:
     * clicking the active tile clears the filter; clicking a different tile
     * switches to it. 'Total' is a sentinel for "all units".
     */
    selectLinenSize(size: string | null): void {
        if (this.selectedLinenSize === size) {
            this.selectedLinenSize = null;
        } else {
            this.selectedLinenSize = size;
        }
    }

    /**
     * Filtered list of per-unit linen rows based on selectedLinenSize.
     * If null or 'Total', returns the full list. Otherwise returns only units
     * whose bedCounts[size] > 0.
     */
    /**
     * Memoized filtered list of linen rows. Angular calls this from the
     * template on every change-detection cycle; without memoization, .filter()
     * allocated a fresh array each call, forcing every *ngFor row to re-render
     * even when nothing actually changed. With 14 sections firing zone.run()
     * + detectChanges() back-to-back during load, the compounded re-render
     * cost was a real cause of the browser's "Page Unresponsive" warning.
     *
     * Cache invalidates only when:
     *   - the underlying byUnit reference changes (new dashboard load)
     *   - selectedLinenSize changes (user clicked a different size tile)
     */
    private _linenUnitsCacheKey: string | null = null;
    private _linenUnitsCacheValue: any[] = [];

    // In-memory sort state for the per-unit linen detail table. Default sort is
    // by check-in date ascending — matches what the backend already returns, so
    // the initial render doesn't flicker.
    linenSortField: string = 'checkInDate';
    linenSortDir: 1 | -1 = 1;

    /**
     * Click handler on the linen-table column headers. Same column => flip
     * direction; new column => switch to it and reset to ascending. Cache key
     * is invalidated so the next linenUnits() call re-sorts.
     */
    sortLinenBy(field: string): void {
        if (this.linenSortField === field) {
            this.linenSortDir = this.linenSortDir === 1 ? -1 : 1;
        } else {
            this.linenSortField = field;
            this.linenSortDir = 1;
        }
        this._linenUnitsCacheKey = null;
    }

    /** Unicode arrow indicator shown next to the active column header. */
    linenSortIcon(field: string): string {
        if (this.linenSortField !== field) return '';
        return this.linenSortDir === 1 ? '▲' : '▼';   // ▲ / ▼
    }

    /** Map a unit row + field name to the sortable value. Returns primitives
     *  only (string or number) so JavaScript's < / > comparison is well-defined. */
    private linenSortValue(u: any, field: string): string | number {
        if (!u) return '';
        switch (field) {
            case 'property':     return u.propertyCode || '';
            // Sort the Guest column by LAST NAME — matches what's actually
            // displayed in the cell (first name was dropped per manager request).
            case 'guest':        return String(u.guestLastName || '').toLowerCase();
            case 'checkInDate':  return u.checkInDate || '';
            case 'bedrooms':     return u.bedrooms != null ? u.bedrooms : -1;
            case 'sofabed':      return u.sofabedCount || 0;
            case 'bathTowels':   return u.bathTowels || 0;
            case 'handTowels':   return u.handTowels || 0;
            case 'faceTowels':   return u.faceTowels || 0;
            case 'beds':         return u.totalBeds || 0;
            case 'king':         return u.pillowsBySize?.King  || 0;
            case 'queen':        return u.pillowsBySize?.Queen || 0;
            case 'full':         return u.pillowsBySize?.Full  || 0;
            case 'twin':         return u.pillowsBySize?.Twin  || 0;
            case 'totalPillows': return u.totalPillows || 0;
            case 'totalSheets':  return u.totalSheets  || 0;
            default:             return '';
        }
    }

    linenUnits(): any[] {
        const source = (this.data && this.data.housekeepingLinen && this.data.housekeepingLinen.byUnit) || [];
        // Cache key includes filter selection AND sort state so the cache
        // invalidates correctly when the manager clicks a different size tile
        // OR a different column header.
        const key = source.length + '|' + (this.selectedLinenSize || '')
                  + '|' + this.linenSortField + '|' + this.linenSortDir;
        if (key === this._linenUnitsCacheKey && this._linenUnitsCacheValue.length <= source.length) {
            return this._linenUnitsCacheValue;
        }
        let value: any[];
        if (!this.selectedLinenSize || this.selectedLinenSize === 'Total') {
            value = source.slice();   // clone before sort so the source isn't mutated
        } else {
            value = source.filter((u: any) => (u.bedCounts && u.bedCounts[this.selectedLinenSize!] > 0));
        }
        // Apply sort. linenSortValue returns primitives so the comparison is
        // safe across all column types (string for property/guest/date, number
        // for everything else).
        const dir = this.linenSortDir;
        const field = this.linenSortField;
        value.sort((a, b) => {
            const av = this.linenSortValue(a, field);
            const bv = this.linenSortValue(b, field);
            if (av < bv) return -dir;
            if (av > bv) return  dir;
            return 0;
        });
        this._linenUnitsCacheKey = key;
        this._linenUnitsCacheValue = value;
        return value;
    }

    private metricTitle(metric: string): string {
        const map: { [k: string]: string } = {
            arrivals:           'Arrivals',
            departures:         'Departures',
            occupied:           'Occupied Rooms',
            vacantClean:        'Vacant (Clean) Rooms',
            outOfOrder:         'Out of Order Rooms',
            cleaningBacklog:    'Cleaning Backlog',
            guestsOnProperty:   'Guests on Property',
            futonsOut:          'Futons Out',
            rollawaysOut:       'Rollaways Out',
            cancellations:      'Cancellations',
            bookingsCreated:    'Bookings Created',
            sameDayTurnovers:   'Same-Day Turnovers',
            priorityRooms:      'Priority Rooms',
            reviews:            'Reviews',
            unitsUnsetStatus:   'Units with Unset / Unknown Status',
            allUnits:           'All Active Units',
            unitsDirty:         'Dirty Units',
            unitsHousekeeping:  'Units in Housekeeping',
            unitsInspection:    'Units in Inspection',
            futonsEligible:     'Futon-Eligible Units (Available)',
            rollawaysEligible:  'Rollaway-Eligible Units (Available)',
            futonsRevenue:      'Futon Revenue Line Items (30d)',
            rollawaysRevenue:   'Rollaway Revenue Line Items (30d)',
        };
        return map[metric] || metric;
    }

    // ---------- Display helpers ----------
    fmtNum(v: any): string {
        if (v == null) return '—';
        const n = typeof v === 'number' ? v : Number(v);
        if (isNaN(n)) return '—';
        return n.toLocaleString('en-US');
    }

    fmtPct(v: any): string {
        if (v == null) return '—';
        const n = typeof v === 'number' ? v : Number(v);
        if (isNaN(n)) return '—';
        return n.toFixed(1) + '%';
    }

    fmtCurrency(v: any): string {
        if (v == null) return '—';
        const n = typeof v === 'number' ? v : Number(v);
        if (isNaN(n)) return '—';
        return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
    }

    fmtDelta(v: any): string {
        if (v == null) return '';
        const n = typeof v === 'number' ? v : Number(v);
        if (isNaN(n)) return '';
        const sign = n > 0 ? '+' : '';
        return `${sign}${n.toFixed(1)} pts WoW`;
    }

    deltaClass(v: any): string {
        if (v == null) return '';
        const n = typeof v === 'number' ? v : Number(v);
        if (isNaN(n) || n === 0) return '';
        return n > 0 ? 'up' : 'down';
    }

    healthClass(section: any): string {
        if (!section) return 'neutral';
        const status = section.status || section.rollup;
        if (status === 'green') return 'win';
        if (status === 'amber') return 'warning';
        if (status === 'red') return 'danger';
        return 'neutral';
    }

    /**
     * Border-left color class for equipment cards — flips amber at >=70%, red at >=90%.
     * Returns a CSS class to apply via [ngClass].
     */
    poolUtilClass(card: any): string {
        if (!card || card.utilization == null) return 'kbma-kpi--info';
        const util = Number(card.utilization);
        if (util >= 90) return 'kbma-kpi--danger';
        if (util >= 70) return 'kbma-kpi--warning';
        return 'kbma-kpi--success';
    }

    severityClass(sev: string): string {
        switch ((sev || '').toUpperCase()) {
            case 'CRITICAL': return 'kbma-badge--overdue';
            case 'HIGH':     return 'kbma-badge--loss';
            case 'MEDIUM':   return 'kbma-badge--stale';
            default:         return 'kbma-badge--inactive';
        }
    }

    /**
     * Aggregated totals across every row of the Operations-by-Location table,
     * shaped like a synthetic location row so the existing mixBarStyle and
     * template bindings work without changes.
     *
     * Pool columns need dedup care:
     *  - Futons: Maui locations all expose the same pool=10 (futonsShared=true);
     *    non-Maui rows have pool=0.
     *  - Rollaways: ALL locations share a single warehouse pool=20
     *    (rollawaysShared=true on every row).
     * Plain summing would over-count both. The rule below counts each shared
     * pool exactly once.
     */
    locationTotals(): any {
        const rows = (this.data && (this.data as any).locations) || [];
        if (!rows.length) return null;
        let totalUnits = 0, occupied = 0, arrivals = 0, departures = 0, guests = 0;
        let futonsOut = 0, rollawaysOut = 0;
        let mixDirty = 0, mixHK = 0, mixInsp = 0, mixReady = 0;
        let futonsPool = 0, rollawaysPool = 0;
        let physicalOccupied = 0, physicalNotAvailable = 0;
        const sharedFutonPoolsSeen = new Set<number>();
        const sharedRollawayPoolsSeen = new Set<number>();
        for (const r of rows) {
            totalUnits           += (r.totalUnits           || 0);
            occupied             += (r.occupied             || 0);
            arrivals             += (r.arrivals             || 0);
            departures           += (r.departures           || 0);
            guests               += (r.guests               || 0);
            futonsOut            += (r.futonsOut            || 0);
            rollawaysOut         += (r.rollawaysOut         || 0);
            physicalOccupied     += (r.physicalOccupied     || 0);
            physicalNotAvailable += (r.physicalNotAvailable || 0);
            if (r.futonsShared) {
                // Maui rows share one pool — count each unique shared pool once.
                if (!sharedFutonPoolsSeen.has(r.futonsPool)) {
                    futonsPool += (r.futonsPool || 0);
                    sharedFutonPoolsSeen.add(r.futonsPool || 0);
                }
            } else {
                futonsPool += (r.futonsPool || 0);
            }
            if (r.rollawaysShared) {
                // All locations share one warehouse pool — count it once.
                if (!sharedRollawayPoolsSeen.has(r.rollawaysPool)) {
                    rollawaysPool += (r.rollawaysPool || 0);
                    sharedRollawayPoolsSeen.add(r.rollawaysPool || 0);
                }
            } else {
                rollawaysPool += (r.rollawaysPool || 0);
            }
            mixDirty += (r.housekeepingMix?.dirty        || 0);
            mixHK    += (r.housekeepingMix?.housekeeping || 0);
            mixInsp  += (r.housekeepingMix?.inspection   || 0);
            mixReady += (r.housekeepingMix?.ready        || 0);
        }
        return {
            totalUnits, occupied,
            occupancyPct: totalUnits === 0 ? 0 : occupied / totalUnits,
            arrivals, departures, guests,
            futonsOut, futonsPool,
            rollawaysOut, rollawaysPool,
            physicalOccupied, physicalNotAvailable,
            housekeepingMix: {
                dirty: mixDirty, housekeeping: mixHK,
                inspection: mixInsp, ready: mixReady,
            },
        };
    }

    mixBarStyle(loc: any, key: string): { [k: string]: string } {
        if (!loc || !loc.housekeepingMix) return { width: '0%' };
        const total = (loc.housekeepingMix.dirty || 0)
            + (loc.housekeepingMix.housekeeping || 0)
            + (loc.housekeepingMix.inspection || 0)
            + (loc.housekeepingMix.ready || 0);
        if (total === 0) return { width: '0%' };
        const pct = ((loc.housekeepingMix[key] || 0) / total) * 100;
        const colorMap: any = {
            dirty:        OperationsDashboardComponent.COLOR.dirty,
            housekeeping: OperationsDashboardComponent.COLOR.housekeeping,
            inspection:   OperationsDashboardComponent.COLOR.inspection,
            ready:        OperationsDashboardComponent.COLOR.ready,
        };
        return { width: `${pct}%`, background: colorMap[key] };
    }

    /**
     * True when every cleaning-lifecycle bucket (Dirty / Housekeeping /
     * Inspection / Ready) is zero for this row — which would otherwise render
     * as a blank bar. Used by the template to swap the bar for an idle-state
     * micro-label so the empty cell doesn't read as a data-loading bug.
     */
    isHkMixIdle(loc: any): boolean {
        const m = loc?.housekeepingMix;
        if (!m) return true;
        return ((m.dirty || 0) + (m.housekeeping || 0)
              + (m.inspection || 0) + (m.ready || 0)) === 0;
    }

    /**
     * Count of units at {@code loc} whose physical occupancyStatus is NULL or
     * an unknown code — i.e. NOT in {OCCUPIED, GUEST_CHECKED_IN, NOT_AVAILABLE}
     * once the cleaning lifecycle has already been confirmed empty by
     * {@link #isHkMixIdle}. Used both by the idle label and by the
     * "X unset → drill" link.
     */
    hkUnsetCount(loc: any): number {
        if (!loc) return 0;
        const total = loc.totalUnits || 0;
        const occ   = loc.physicalOccupied || 0;
        const na    = loc.physicalNotAvailable || 0;
        return Math.max(0, total - occ - na);
    }

    /**
     * Open the drilldown showing the specific units with NULL / unknown
     * occupancyStatus at this location. Wired from the "X unset" link in
     * the Housekeeping-Mix idle micro-label so the manager can spot which
     * properties never had their Breezeway sync fire.
     */
    drilldownUnsetUnits(loc: any): void {
        const locId = (loc && loc.locationId != null) ? loc.locationId : null;
        this.drilldown('unitsUnsetStatus', 'Units with Unset Status', locId);
    }

    // ---------- Navigation actions ----------
    openArrivalsAgent(): void { this.router.navigate(['/arrivals-agent']); }
    openFutureOccupancy(): void { this.router.navigate(['/future-occupancy-dashboard']); }
    openVacancyReport(): void { this.router.navigate(['/vacancy-report']); }
    openOwnerStatements(): void { this.router.navigate(['/owner-statements']); }

    /**
     * Drill into the Pickup (bookings created) card. Each of the four stat
     * cells — In Range, Today, Yesterday, Last 7d — represents a different
     * date window over `str_order.saleDate` with `status IN ('OP','OC')`.
     * Mapping mirrors the backend's `buildRevenueSignals.pickup` exactly so
     * the drilldown row count = the displayed count.
     *
     * "Pickup" is hotel-industry jargon for booking pace — NOT vehicle pickup.
     * KBM has no vehicle pickup product; we're counting new confirmed reservations
     * created within the bucket's window.
     */
    pickupDrilldown(bucket: 'range' | 'today' | 'yesterday' | 'last7d'): void {
        const end = moment(this.endDate).startOf('day');
        let start: moment.Moment;
        let endForBucket: moment.Moment;
        let label: string;
        switch (bucket) {
            case 'today':
                start = end.clone();
                endForBucket = end.clone();
                label = 'Bookings Created — Today';
                break;
            case 'yesterday':
                start = end.clone().subtract(1, 'day');
                endForBucket = end.clone().subtract(1, 'day');
                label = 'Bookings Created — Yesterday';
                break;
            case 'last7d':
                // Rolling 7 days, excludes today — matches backend
                // countBookingsCreatedInRange(endDate-7, endDate-1).
                start = end.clone().subtract(7, 'day');
                endForBucket = end.clone().subtract(1, 'day');
                label = 'Bookings Created — Last 7 Days';
                break;
            case 'range':
            default:
                start = moment(this.startDate).startOf('day');
                endForBucket = end.clone();
                label = 'Bookings Created — Range';
                break;
        }
        this.drilldown('bookingsCreated', label, undefined, start.toDate(), endForBucket.toDate());
    }

    /**
     * Drill into the Review Score — Recent Days card. Two entry points:
     *   - Click on a single trend row → that day only (start = end = rowDate)
     *   - Click on the card header / "View all" → full 30-day window
     *
     * The backend `reviewScoreTrend30d` covers `[endDate − 30 .. endDate)`, so
     * the card-level drilldown uses exactly that window.
     */
    reviewsDrilldown(rowDate?: string | Date): void {
        const end = moment(this.endDate).startOf('day');
        let start: moment.Moment;
        let endForWindow: moment.Moment;
        let label: string;
        if (rowDate) {
            const d = moment(rowDate).startOf('day');
            start = d.clone();
            endForWindow = d.clone();
            label = `Reviews — ${d.format('MMM D, YYYY')}`;
        } else {
            // Card-level drilldown: full trend window. reviewScoreTrend30d on the
            // backend uses [endDate-30 .. endDate), so mirror that here.
            start = end.clone().subtract(30, 'day');
            endForWindow = end.clone().subtract(1, 'day');
            label = 'Reviews — Last 30 Days';
        }
        this.drilldown('reviews', label, undefined, start.toDate(), endForWindow.toDate());
    }

    /**
     * Open the Arrivals Agent board in a new tab, pre-selected to {@code date}
     * and view type. Low-level helper — usually called via
     * {@link #openArrivalsAgentForTile} which smart-picks the day from the
     * dashboard's currently-selected range. The board reads ?date= and ?type=
     * on init — see arrivals-agent-board.component.ts ngOnInit.
     */
    openArrivalsAgentForDate(date: Date, type: 'arrivals' | 'departures' = 'arrivals'): void {
        const d = moment(date).format('YYYY-MM-DD');
        window.open(`/arrivals-agent?date=${d}&type=${type}`, '_blank', 'noopener');
    }

    /**
     * Tile-click entry point for the Arrivals / Departures tiles. The Arrivals
     * Agent board is single-day by design, but the tile count can be over a
     * multi-day range (MTD, Next Week, custom calendar pick). Pick the most
     * actionable day from the range:
     *   1. today, if today ∈ [startDate, endDate]   — what the manager probably wants
     *   2. startDate, if the range is forward-looking   — manager steps forward
     *   3. endDate, otherwise (past range)              — most recent day of activity
     * Single-day ranges fall through naturally (all three pick the same day).
     */
    openArrivalsAgentForTile(type: 'arrivals' | 'departures' = 'arrivals'): void {
        this.openArrivalsAgentForDate(this.pickBoardDayFromRange(), type);
    }

    private pickBoardDayFromRange(): Date {
        const today = moment().startOf('day');
        const start = moment(this.startDate).startOf('day');
        const end   = moment(this.endDate).startOf('day');
        if (!today.isBefore(start) && !today.isAfter(end)) return today.toDate();
        if (start.isAfter(today))                          return start.toDate();
        return end.toDate();
    }

    /**
     * Open the canonical /reservations list page in a new tab, pre-filtered to
     * the SAME reservation set the Occupancy tile counts:
     *   - status IN (OP, NA, OO, OG)  — canonical Occupancy Summary Report set
     *   - stay overlaps [startDate, endDate]  — the dashboard's current range
     *   - location filter, if one is active on the dashboard
     *
     * The reservation list reads these from the URL query string (see
     * reservation-list.component.ts ngOnInit) and pre-populates its
     * advanceSearchDataFilter before the first lazy load, so the page lands
     * already filtered.
     */
    openOccupancyReservations(): void {
        this.openReservationsList({
            status: 'OP,NA,OO,OG',
            stayDateStart: this.startDate,
            stayDateEnd:   this.endDate,
        });
    }

    /**
     * Canonical "open /reservations in a new tab with these filters" helper.
     * Every reservation-shaped drilldown tile / cell on the dashboard goes
     * through here so we have ONE place enforcing the listingType=1 +
     * listingStatus=0 scope (active child STR units only, no composites).
     * Without those, the reservation list double-counts composite parents
     * and inactive listings, drifting the row total above the tile.
     *
     * locationId defaults to whatever filter is active on the dashboard,
     * but the caller can pass an explicit override (e.g. a per-location
     * cell click overrides with that row's locationId).
     */
    openReservationsList(opts: {
        status?: string;
        stayDateStart?: Date | null;
        stayDateEnd?:   Date | null;
        checkInDateStart?:  Date | null;
        checkInDateEnd?:    Date | null;
        checkOutDateStart?: Date | null;
        checkOutDateEnd?:   Date | null;
        saleDateStart?: Date | null;
        saleDateEnd?:   Date | null;
        locationId?:    number | null;
    }): void {
        const params: string[] = [];
        const fmt = (d: Date | null | undefined) => d ? moment(d).format('YYYY-MM-DD') : null;
        if (opts.status)            params.push(`status=${encodeURIComponent(opts.status)}`);
        const stayS = fmt(opts.stayDateStart); if (stayS) params.push(`stayDateStart=${stayS}`);
        const stayE = fmt(opts.stayDateEnd);   if (stayE) params.push(`stayDateEnd=${stayE}`);
        const cinS  = fmt(opts.checkInDateStart);  if (cinS)  params.push(`checkInDateStart=${cinS}`);
        const cinE  = fmt(opts.checkInDateEnd);    if (cinE)  params.push(`checkInDateEnd=${cinE}`);
        const coutS = fmt(opts.checkOutDateStart); if (coutS) params.push(`checkOutDateStart=${coutS}`);
        const coutE = fmt(opts.checkOutDateEnd);   if (coutE) params.push(`checkOutDateEnd=${coutE}`);
        const saleS = fmt(opts.saleDateStart); if (saleS) params.push(`saleDateStart=${saleS}`);
        const saleE = fmt(opts.saleDateEnd);   if (saleE) params.push(`saleDateEnd=${saleE}`);
        // Always scope to active child STR units — mirrors what the dashboard's
        // overlap query enforces.
        params.push(`listingType=1`);
        params.push(`listingStatus=0`);
        const locId = (opts.locationId !== undefined) ? opts.locationId : this.selectedLocationId;
        if (locId != null) params.push(`locationId=${locId}`);
        window.open(`/reservations?${params.join('&')}`, '_blank', 'noopener');
    }

    // ---- Reservation-shaped tile/cell handlers ----
    // All route through openReservationsList so /reservations is the ONE
    // proprietary screen replaced (Josh's directive: no per-dashboard popups).

    /** Departures tile + per-location Departures cell. */
    openReservationsForDepartures(date?: Date, locationIdOverride?: number | null): void {
        const start = date ?? this.startDate;
        const end   = date ?? this.endDate;
        this.openReservationsList({
            status: 'OP,OC,OG,OO',
            checkOutDateStart: start,
            checkOutDateEnd:   end,
            locationId: locationIdOverride,
        });
    }

    /** Arrivals per-location cell (the KPI tile still opens the Agent board
     *  via openArrivalsAgentForTile because that's the operational screen). */
    openReservationsForArrivals(date?: Date, locationIdOverride?: number | null): void {
        const start = date ?? this.startDate;
        const end   = date ?? this.endDate;
        this.openReservationsList({
            status: 'OP,OC,OG,OO',
            checkInDateStart: start,
            checkInDateEnd:   end,
            locationId: locationIdOverride,
        });
    }

    /** Bookings Created — saleDate window. Bucket maps to range, today,
     *  yesterday, last 7d per the existing pickupDrilldown semantics. */
    openReservationsForBookings(bucket: 'range' | 'today' | 'yesterday' | 'last7d'): void {
        const end = moment(this.endDate).startOf('day');
        let start: moment.Moment;
        let endForBucket: moment.Moment;
        switch (bucket) {
            case 'today':     start = end.clone(); endForBucket = end.clone(); break;
            case 'yesterday': start = end.clone().subtract(1, 'day'); endForBucket = end.clone().subtract(1, 'day'); break;
            case 'last7d':    start = end.clone().subtract(7, 'day'); endForBucket = end.clone().subtract(1, 'day'); break;
            case 'range':
            default:          start = moment(this.startDate).startOf('day'); endForBucket = end.clone(); break;
        }
        this.openReservationsList({
            status: 'OP,OC',
            saleDateStart: start.toDate(),
            saleDateEnd:   endForBucket.toDate(),
        });
    }

    /** Cancellations card — stays cancelled in the dashboard window. No
     *  cancelDate filter exists on the reservation list yet, so the closest
     *  approximation is status=OX + stayDateStart/End matching the stays
     *  that would have happened in the window. Worth flagging: this misses
     *  cancellations whose original stay was outside the dashboard range. */
    openReservationsForCancellations(): void {
        this.openReservationsList({
            status: 'OX',
            stayDateStart: this.startDate,
            stayDateEnd:   this.endDate,
        });
    }

    /** Maintenance section — units off-market today (status NA). */
    openReservationsForMaintenance(locationIdOverride?: number | null): void {
        this.openReservationsList({
            status: 'NA',
            stayDateStart: this.endDate,
            stayDateEnd:   this.endDate,
            locationId: locationIdOverride,
        });
    }

    /** Future Occupancy heat-strip cell — clicked (date, location) → all
     *  reservations whose stay overlaps that day at that location. */
    openReservationsForOccupancyOnDate(date: Date, locationIdOverride?: number | null): void {
        this.openReservationsList({
            status: 'OP,NA,OO,OG',
            stayDateStart: date,
            stayDateEnd:   date,
            locationId: locationIdOverride,
        });
    }

    /**
     * "All Active Units" — opens the canonical /property-list page filtered
     * to active listings, optionally scoped to a single location. Replaces
     * the proprietary 'allUnits' drilldown so the manager works the same
     * properties screen used everywhere else in admin.
     *
     * Property-list reads ?locationId=, ?listingStatus=, ?listingType= from
     * the URL and pre-applies them to the PrimeNG table filters before the
     * first lazy load. All three defaults mirror the dashboard's
     * activeUnitsByLocation query so the row count on /property-list
     * tracks the tile exactly:
     *   • listingStatus = 0   → Active listings only
     *   • listingType   = 1   → real Short-Term Rental units only
     *                           (excludes composite parents = type 2)
     *   • locationId    = X   → optional, per-row when scoped
     */
    openPropertyListForLocation(locationIdOverride?: number | null): void {
        const params: string[] = [`listingStatus=0`, `listingType=1`];
        const locId = (locationIdOverride !== undefined) ? locationIdOverride : this.selectedLocationId;
        if (locId != null) params.push(`locationId=${locId}`);
        window.open(`/property-list?${params.join('&')}`, '_blank', 'noopener');
    }

    // ---------- Chart rendering ----------
    private destroyCharts(): void {
        this.charts.forEach(c => { try { c.destroy(); } catch (e) { /* swallow */ } });
        this.charts = [];
    }

    /**
     * Render every chart in one shot — used when all section data is already
     * present (e.g. tab switch back to overview). Per-section progressive
     * rendering is driven by the helpers below.
     */
    private renderCharts(): void {
        if (!this.data) return;
        this.renderEquipmentCharts();
        this.renderTrendCharts();
        this.renderRevenueCharts();
        this.renderFoCharts();
    }

    /** Charts that depend on the `equipment` section payload. */
    private renderEquipmentCharts(): void {
        const eq = (this.data && this.data.equipment) || {};
        this.renderEquipmentDonut(this.futonDonutEl,    eq.futon);
        this.renderEquipmentDonut(this.rollawayDonutEl, eq.rollaway);
        this.renderSparkline(this.futonSparkEl,    eq.futon    && eq.futon.sparkline14d);
        this.renderSparkline(this.rollawaySparkEl, eq.rollaway && eq.rollaway.sparkline14d);
    }

    /** Charts that depend on the `trends` section payload. */
    private renderTrendCharts(): void {
        const trends = (this.data && this.data.trends) || {};
        this.renderOccupancyLine(this.occLineEl, trends.occupancyByLocation30d);
        this.renderBeddingLine(this.beddingLineEl, trends.beddingDemand90d);
        this.renderCancelBar(this.cancelBarEl, trends.cancellations30d);
    }

    /** Charts that depend on the `revenueSignals` section payload. */
    private renderRevenueCharts(): void {
        const revenue = (this.data && this.data.revenueSignals) || {};
        // Backend key is `channelMix` (the previous `channelMixToday` key was a
        // stale reference and was the reason the Channel Mix card sat empty).
        this.renderChannelDonut(this.channelDonutEl, revenue.channelMix);
    }

    /** Charts that depend on the `futureOccupancyReport` section payload. */
    private renderFoCharts(): void {
        if (this.foTab !== 'overview') return;
        const fo = (this.data && this.data.futureOccupancyReport) || {};
        this.renderFoMonthlyBar(this.foMonthlyBarEl, fo.monthlyData);
        this.renderFoTypePie(this.foTypePieEl, fo.byTypeData);
        this.renderFoCheckLine(this.foCheckLineEl, fo.dailyCheckIns, fo.dailyCheckOuts);
        this.renderFoByBed(this.foCheckInByBedEl,  fo.dailyCheckInsByBedroom);
        this.renderFoByBed(this.foCheckOutByBedEl, fo.dailyCheckOutsByBedroom);
    }

    /** Future Occupancy Report — switch tabs. Re-render charts when going back to overview. */
    foSwitchTab(tab: 'overview' | 'reservations'): void {
        this.foTab = tab;
        if (tab === 'overview') {
            setTimeout(() => this.renderCharts(), 0);
        }
    }

    /** Bar chart: per-month occupancy % (matches the standalone report's top chart). */
    private renderFoMonthlyBar(el: ElementRef<HTMLCanvasElement> | undefined, monthly: any[] | undefined): void {
        if (!el?.nativeElement || !monthly?.length) return;
        const c = new Chart(el.nativeElement, {
            type: 'bar',
            data: {
                labels: monthly.map(m => m.monthLabel),
                datasets: [{
                    label: 'Occupancy %',
                    data: monthly.map(m => m.occupancyPct || 0),
                    backgroundColor: OperationsDashboardComponent.COLOR.info,
                    borderWidth: 0,
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    datalabels: {
                        anchor: 'center', align: 'center', color: '#fff',
                        font: { size: 11, weight: 700 },
                        formatter: (v: any) => `${Number(v).toFixed(1)}%`,
                    },
                    tooltip: { enabled: true, callbacks: { label: (ctx: any) => `${ctx.parsed.y.toFixed(1)}%` } },
                },
                scales: {
                    x: { ticks: { font: { size: 11 } } },
                    y: { beginAtZero: true, max: 100, ticks: { font: { size: 10 }, callback: (v) => `${v}%` } },
                },
            },
        });
        this.charts.push(c);
    }

    /** Pie chart: nights by reservation type (Owner / Owner Guest / Guest / Maintenance). */
    private renderFoTypePie(el: ElementRef<HTMLCanvasElement> | undefined, byType: any[] | undefined): void {
        if (!el?.nativeElement || !byType?.length) return;
        // Palette mirrors the standalone report's color scheme
        const colorMap: any = {
            'Owner':       '#EC4899',
            'Owner Guest': '#FBCFE8',
            'Guest':       '#D1D5DB',
            'Maintenance': '#6B7280',
        };
        const c = new Chart(el.nativeElement, {
            type: 'pie',
            data: {
                labels: byType.map(t => t.resTypeName),
                datasets: [{
                    data: byType.map(t => t.numNights || 0),
                    backgroundColor: byType.map(t => colorMap[t.resTypeName] || OperationsDashboardComponent.COLOR.neutral),
                    borderWidth: 0,
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: true, position: 'bottom', labels: { font: { size: 11 }, boxWidth: 12 } },
                    datalabels: {
                        color: '#fff', font: { size: 11, weight: 700 },
                        formatter: (v: any, ctx: any) => {
                            const total = ctx.chart.data.datasets[0].data.reduce((a: number, b: number) => a + b, 0);
                            if (!total) return '';
                            const pct = (v / total) * 100;
                            return pct >= 5 ? `${pct.toFixed(0)}%` : '';
                        },
                    },
                    tooltip: { enabled: true },
                },
            },
        });
        this.charts.push(c);
    }

    /** Daily check-in vs check-out dual-line chart. */
    private renderFoCheckLine(el: ElementRef<HTMLCanvasElement> | undefined, checkIns: any[] | undefined, checkOuts: any[] | undefined): void {
        if (!el?.nativeElement || (!checkIns?.length && !checkOuts?.length)) return;
        const labels = (checkIns || checkOuts || []).map((p: any) => moment(p.date).format('M/D'));
        const c = new Chart(el.nativeElement, {
            type: 'line',
            data: {
                labels,
                datasets: [
                    {
                        label: 'Check-ins',
                        data: (checkIns || []).map((p: any) => p.count),
                        borderColor: OperationsDashboardComponent.COLOR.info,
                        backgroundColor: 'transparent',
                        tension: 0.25, pointRadius: 2, borderWidth: 2,
                    },
                    {
                        label: 'Check-outs',
                        data: (checkOuts || []).map((p: any) => p.count),
                        borderColor: OperationsDashboardComponent.COLOR.danger,
                        backgroundColor: 'transparent',
                        tension: 0.25, pointRadius: 2, borderWidth: 2,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: true, position: 'top', labels: { font: { size: 11 }, boxWidth: 12 } },
                    datalabels: { display: false },
                    tooltip: { mode: 'index', intersect: false },
                },
                scales: {
                    x: { ticks: { font: { size: 10 }, maxRotation: 0, autoSkip: true, maxTicksLimit: 15 } },
                    y: { beginAtZero: true, ticks: { font: { size: 10 }, stepSize: 5 } },
                },
            },
        });
        this.charts.push(c);
    }

    /** Stacked / multi-line chart for daily check-in (or check-out) volume by bedroom count. */
    private renderFoByBed(el: ElementRef<HTMLCanvasElement> | undefined, series: any[] | undefined): void {
        if (!el?.nativeElement || !series?.length) return;
        // Extract bedroom keys from the first point (Studio, 1 Bed, 2 Bed, …)
        const bedroomKeys = Object.keys(series[0]?.bedrooms || {});
        const palette = ['#8B5CF6', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#EC4899', '#6366F1', '#0EA5E9'];
        const datasets = bedroomKeys.map((bk, i) => ({
            label: bk,
            data: series.map((p: any) => (p.bedrooms && p.bedrooms[bk]) || 0),
            borderColor: palette[i % palette.length],
            backgroundColor: 'transparent',
            tension: 0.25, pointRadius: 1, borderWidth: 1.5,
        }));
        const c = new Chart(el.nativeElement, {
            type: 'line',
            data: { labels: series.map((p: any) => moment(p.date).format('M/D')), datasets },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: true, position: 'top', labels: { font: { size: 10 }, boxWidth: 10 } },
                    datalabels: { display: false },
                    tooltip: { mode: 'index', intersect: false },
                },
                scales: {
                    x: { ticks: { font: { size: 9 }, maxRotation: 0, autoSkip: true, maxTicksLimit: 12 } },
                    y: { beginAtZero: true, ticks: { font: { size: 9 }, stepSize: 3 } },
                },
            },
        });
        this.charts.push(c);
    }

    private renderEquipmentDonut(el: ElementRef<HTMLCanvasElement> | undefined, card: any): void {
        if (!el?.nativeElement || !card) return;
        const out = card.totalOut || 0;
        const available = card.available || 0;
        const c = new Chart(el.nativeElement, {
            type: 'doughnut',
            data: {
                labels: ['Booked', 'Available'],
                datasets: [{
                    data: [out, available],
                    backgroundColor: [
                        OperationsDashboardComponent.COLOR.info,
                        OperationsDashboardComponent.COLOR.neutral,
                    ],
                    borderWidth: 0,
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                cutout: '70%',
                plugins: {
                    legend: { display: true, position: 'bottom', labels: { font: { size: 11 } } },
                    datalabels: { display: false },
                    tooltip: { enabled: true },
                },
            },
        });
        this.charts.push(c);
    }

    private renderSparkline(el: ElementRef<HTMLCanvasElement> | undefined, series: any[] | undefined): void {
        if (!el?.nativeElement || !series) return;
        const c = new Chart(el.nativeElement, {
            type: 'line',
            data: {
                labels: series.map(s => moment(s.date).format('M/D')),
                datasets: [{
                    data: series.map(s => s.out || 0),
                    borderColor: OperationsDashboardComponent.COLOR.info,
                    backgroundColor: 'rgba(59,130,246,0.10)',
                    fill: true,
                    tension: 0.3,
                    pointRadius: 0,
                    borderWidth: 2,
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false }, datalabels: { display: false } },
                scales: { x: { display: false }, y: { display: false } },
            },
        });
        this.charts.push(c);
    }

    private renderOccupancyLine(el: ElementRef<HTMLCanvasElement> | undefined, locations: any[] | undefined): void {
        if (!el?.nativeElement || !locations || !locations.length) return;
        const labels = locations[0].series.map((p: any) => moment(p.date).format('M/D'));
        const palette = [
            OperationsDashboardComponent.COLOR.info,
            OperationsDashboardComponent.COLOR.win,
            OperationsDashboardComponent.COLOR.warning,
            OperationsDashboardComponent.COLOR.danger,
            OperationsDashboardComponent.COLOR.neutral,
            '#6366F1', // indigo for 6th
        ];
        const datasets = locations.map((loc, i) => ({
            label: loc.locationName,
            data: loc.series.map((p: any) => p.occupancyPct),
            borderColor: palette[i % palette.length],
            backgroundColor: 'transparent',
            tension: 0.25,
            pointRadius: 0,
            borderWidth: 2,
        }));
        const c = new Chart(el.nativeElement, {
            type: 'line',
            data: { labels, datasets },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: true, position: 'bottom', labels: { font: { size: 11 }, boxWidth: 12 } },
                    datalabels: { display: false },
                },
                scales: {
                    x: { ticks: { font: { size: 10 } } },
                    y: { beginAtZero: true, ticks: { font: { size: 10 }, callback: (v) => `${v}%` } },
                },
            },
        });
        this.charts.push(c);
    }

    private renderBeddingLine(el: ElementRef<HTMLCanvasElement> | undefined, series: any[] | undefined): void {
        if (!el?.nativeElement || !series) return;
        const c = new Chart(el.nativeElement, {
            type: 'line',
            data: {
                labels: series.map(s => moment(s.date).format('M/D')),
                datasets: [
                    {
                        label: 'Futons',
                        data: series.map(s => s.futon || 0),
                        borderColor: OperationsDashboardComponent.COLOR.info,
                        backgroundColor: 'transparent',
                        tension: 0.25, pointRadius: 0, borderWidth: 2,
                    },
                    {
                        label: 'Rollaways',
                        data: series.map(s => s.rollaway || 0),
                        borderColor: OperationsDashboardComponent.COLOR.warning,
                        backgroundColor: 'transparent',
                        tension: 0.25, pointRadius: 0, borderWidth: 2,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: true, position: 'bottom', labels: { font: { size: 11 }, boxWidth: 12 } },
                    datalabels: { display: false },
                },
                scales: {
                    x: { ticks: { font: { size: 10 } } },
                    y: { beginAtZero: true, ticks: { font: { size: 10 } } },
                },
            },
        });
        this.charts.push(c);
    }

    private renderCancelBar(el: ElementRef<HTMLCanvasElement> | undefined, series: any[] | undefined): void {
        if (!el?.nativeElement || !series) return;
        const c = new Chart(el.nativeElement, {
            type: 'bar',
            data: {
                labels: series.map(s => moment(s.date).format('M/D')),
                datasets: [{
                    label: 'Cancellations',
                    data: series.map(s => s.count || 0),
                    backgroundColor: OperationsDashboardComponent.COLOR.danger,
                    borderWidth: 0,
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false }, datalabels: { display: false } },
                scales: {
                    x: { ticks: { font: { size: 10 } } },
                    y: { beginAtZero: true, ticks: { font: { size: 10 }, stepSize: 1 } },
                },
            },
        });
        this.charts.push(c);
    }

    /**
     * Render the Channel Mix donut from the {@code channelMix} row array
     * (shape: {@code [{ refSrcId, arrivals }, ...]}).
     *
     * Slice labels resolve via {@link #channelLabel} (same constants source as
     * the rest of the app — refSrcImages — so Airbnb / VRBO / Booking.com etc.
     * read the same everywhere). Null {@code refSrcId} → "Direct" (no OTA on
     * the reservation, i.e. kbmresorts.com booking / phone-in / corporate
     * direct).
     * <p>
     * <b>Aggregation by resolved label</b> — KBM has multiple refSrcIds that
     * all resolve to the same human label (e.g. several "VRBO" sub-channels:
     * VRBO API, HomeAway, Stayz). Before this aggregation step, each
     * sub-channel produced its own donut slice, fragmenting VRBO's share
     * across multiple wedges and making the chart underrepresent the channel.
     * We now SUM by the channelLabel string so VRBO (and any other multi-id
     * channel) shows up as one consolidated slice that matches the
     * reservation-list "Channel = VRBO" filter row count.
     * <p>
     * Tooltip shows the channel name, arrival count, and percent of total so
     * the donut is informative without legend hover.
     */
    private renderChannelDonut(el: ElementRef<HTMLCanvasElement> | undefined, mix: any[] | undefined): void {
        if (!el?.nativeElement || !mix || !mix.length) return;
        const palette = [
            OperationsDashboardComponent.COLOR.info,
            OperationsDashboardComponent.COLOR.win,
            OperationsDashboardComponent.COLOR.warning,
            OperationsDashboardComponent.COLOR.danger,
            OperationsDashboardComponent.COLOR.neutral,
            '#6366F1', '#C5A572',
        ];
        // Resolve each raw refSrcId to its human label, then collapse into a
        // single bucket per label. Map iteration preserves insertion order, so
        // the most-frequent channels (the backend already orders by arrivals
        // desc) keep their relative position in the donut.
        const buckets = new Map<string, number>();
        for (const m of mix) {
            let label: string;
            if (m.refSrcId == null) {
                label = 'Direct';
            } else {
                const lbl = this.channelLabel(m.refSrcId);
                label = lbl || `Source ${m.refSrcId}`;
            }
            buckets.set(label, (buckets.get(label) || 0) + (m.arrivals || 0));
        }
        // Sort by count desc so the donut palette aligns with magnitude.
        const sorted = Array.from(buckets.entries()).sort((a, b) => b[1] - a[1]);
        const labels = sorted.map(e => e[0]);
        const values = sorted.map(e => e[1]);
        const total  = values.reduce((a: number, b: number) => a + b, 0);
        const c = new Chart(el.nativeElement, {
            type: 'doughnut',
            data: {
                labels,
                datasets: [{
                    data: values,
                    // Colors keyed off the deduped label list, not the raw mix
                    // array — otherwise the slice colors would no longer line
                    // up with the post-aggregation slice count.
                    backgroundColor: labels.map((_, i) => palette[i % palette.length]),
                    borderWidth: 0,
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                cutout: '60%',
                plugins: {
                    legend: { display: true, position: 'bottom', labels: { font: { size: 11 }, boxWidth: 12 } },
                    datalabels: { display: false },
                    tooltip: {
                        callbacks: {
                            label: (ctx: any) => {
                                const v = ctx.parsed || 0;
                                const pct = total > 0 ? Math.round((v / total) * 100) : 0;
                                return `${ctx.label}: ${v} (${pct}%)`;
                            },
                        },
                    },
                },
            },
        });
        this.charts.push(c);
    }
}
